import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Flex, Grid, Input, Pagination, Tabs, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useCashPlans } from '@/features/cashdesk/cashdeskPlans'
import { DayRangeBar } from '@/features/profile/DayRangeBar'
import { quickRange, tenantRangeWindow, type DayRange } from '@/shared/lib/tenantDay'
import { BatchCodesDrawer } from '@/features/voucher/BatchCodesDrawer'
import { MyActivationsList } from '@/features/voucher/MyActivationsList'
import { MyBatchesTable } from '@/features/voucher/MyBatchesTable'
import { VoucherPrintSheet } from '@/features/voucher/VoucherPrintSheet'
import { useMyActivations, useMyBatches } from '@/features/voucher/useVoucherStock'
import {
  ACTIVATION_PAGE_SIZE,
  BATCH_PAGE_SIZE,
  type VoucherBatchRow,
} from '@/features/voucher/voucherStockApi'
import { grantPlanLabel } from '@/shared/lib/planName'

/** Yetti ustunli jadval — undan kengrog'ida qatorlar bo'shab qoladi */
const PAGE_MAX_WIDTH = 1160

/**
 * «Kartalarim» — egasi talabi (2026-08-24).
 *
 * Sherikning ikki savoli bor va ular BOSHQA-BOSHQA:
 *
 *   1. «Menda qanday kartalar bor, qanchasi qoldi, qachon kuyadi» —
 *      «Partiyalarim» bo'limi (`GET /api/vouchers/my/batches`);
 *   2. «Men qaysi kartalarni ishlatdim» — «Men ishlatganlarim»
 *      (`GET /api/vouchers/my/activations`).
 *
 * <h2>⛔ IKKI RO'YXAT KESISHMASLIGI MUMKIN — va bu NORMAL</h2>
 * `voucher_batch.agent_id` partiya KIMGA BERILGANINI aytadi (narx
 * qotiriladi, zaxira o'sha yerda hisoblanadi) va u AKTIVATSIYAGA
 * TA'SIR QILMAYDI: kartani har qanday agent yoki reseller faollashtira
 * oladi — `voucher.activate` ikkala `PartnerKind` to'plamining bazaviy
 * qismida (`PartnerKind.java:55` va `:92`). Ya'ni ikkinchi ro'yxatda
 * birinchisida yo'q karta chiqishi mumkin va aksincha.
 *
 * Shuning uchun ular BITTA jadvalga qo'shilmadi: qo'shilsa ikki
 * boshqa-boshqa savolning javobi bir-birini buzardi.
 *
 * <h2>⛔ PUL YO'Q va qo'shilmaydi</h2>
 * Vaucher ledgerda umuman yozilmaydi (egasi qarori) — shuning uchun bu
 * ekranda daromad, komissiya yoki yig'indi YO'Q. Partiyaning narxi
 * ustunda ko'rinadi, chunki u kartaning ustiga chop etiladi, «qancha
 * ishladim» degan javob sifatida emas.
 *
 * <h2>⚠️ SUKUT DAVR — SHU OY (ikkinchi bo'limda)</h2>
 * «Mening amallarim» da «bugun», chunki u savol kunlik. Karta
 * faollashtirish esa siyrak hodisa va bir kunlik oyna ro'yxatni
 * ko'pincha BO'SH ko'rsatardi — bo'shligi «hech narsa ishlatmadim»
 * bo'lib o'qilardi («Berilgan paketlar» dagi ayni sabab).
 *
 * <h2>⚠️ KATALOG SAHIFADA OLINADI</h2>
 * Paket nomi `/api/catalog/plans` dan keladi, u esa `features/cashdesk`
 * da. `features/*` bir-birini import qilmaydi (07-FRONTEND §6) —
 * shuning uchun ikkalasini SAHIFA bog'laydi (`GrantedPlansPage` dagi
 * ayni naqsh).
 */
export function MyVouchersPage() {
  const { t, i18n } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false
  const plans = useCashPlans()
  const language = i18n.language

  // --- «Partiyalarim» -------------------------------------------------
  const [search, setSearch] = useState('')
  const [batchPage, setBatchPage] = useState(0)
  const batches = useMyBatches(search, batchPage)

  // Qidiruv o'zgarganda sahifa NOLGA: aks holda ikkinchi sahifada
  // turgan operator yangi qidiruvning BO'SH sahifasini ko'rardi va uni
  // «hech narsa topilmadi» deb o'qirdi.
  useEffect(() => {
    setBatchPage(0)
  }, [search])

  // --- «Men ishlatganlarim» -------------------------------------------
  const [range, setRange] = useState<DayRange>(() => quickRange('month'))
  const [activationPage, setActivationPage] = useState(0)
  const window = useMemo(() => tenantRangeWindow(range), [range])
  const activations = useMyActivations(window, activationPage)

  useEffect(() => {
    setActivationPage(0)
  }, [range])

  // --- Kodlar oynasi va chop etish ------------------------------------
  const [openBatch, setOpenBatch] = useState<VoucherBatchRow | null>(null)
  /**
   * ⛔ CHOP ETISH RO'YXATI SAHIFADA TURADI, oynaning ichida emas.
   * Maket `Modal` ni o'z `@media print` qoidasi bilan ochadi
   * (`VoucherPrintSheet`), oyna esa `Drawer` — biri ikkinchisining
   * ichida bo'lsa chop etishda `Drawer` ning qobig'i ham qog'ozga
   * tushardi.
   */
  const [printRequest, setPrintRequest] = useState<{ serials: readonly number[] | null } | null>(null)

  const planLabelFor = useCallback(
    (planId: string | null) => grantPlanLabel({ planId, planName: null }, plans.byId, language),
    [plans.byId, language],
  )

  const batchTotal = batches.page?.total ?? 0
  const activationTotal = activations.page?.total ?? 0

  return (
    <div style={{ maxWidth: isMobile ? undefined : PAGE_MAX_WIDTH, margin: '0 auto' }}>
      {/* Sarlavha faqat kompyuterda — telefonda uni kabinetning yuqori
          qatori ko'rsatadi (`GrantedPlansPage` bilan bir xil qaror). */}
      {isMobile ? null : (
        <div style={{ marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('myVouchers.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('myVouchers.subtitle')}</Typography.Text>
        </div>
      )}

      <Tabs
        items={[
          {
            key: 'batches',
            label: t('myVouchers.tab.batches'),
            children: (
              <Flex vertical gap={16}>
                <Input.Search
                  allowClear
                  size="large"
                  style={{ maxWidth: isMobile ? undefined : 360 }}
                  placeholder={t('myVouchers.searchPlaceholder')}
                  onSearch={setSearch}
                  // ⚠️ `onSearch` — Enter va tugma. Har harfda so'rov
                  // yuborish kassirning sekin tarmog'ida ro'yxatni
                  // «sakrab turadigan» qilardi.
                />

                <Card size="small" title={t('myVouchers.batchesTitle')}>
                  <MyBatchesTable
                    state={batches}
                    onSelect={setOpenBatch}
                    planLabel={(batch) => planLabelFor(batch.planId)}
                    filtered={search.trim() !== ''}
                  />
                </Card>

                {batchTotal > BATCH_PAGE_SIZE ? (
                  <Pagination
                    current={batchPage + 1}
                    pageSize={BATCH_PAGE_SIZE}
                    total={batchTotal}
                    showSizeChanger={false}
                    onChange={(p) => setBatchPage(p - 1)}
                  />
                ) : null}
              </Flex>
            ),
          },
          {
            key: 'activations',
            label: t('myVouchers.tab.activations'),
            children: (
              <Flex vertical gap={16}>
                <DayRangeBar range={range} onChange={setRange} />

                <Card size="small" title={t('myVouchers.activationsTitle')}>
                  <MyActivationsList
                    state={activations}
                    planLabel={(row) => planLabelFor(row.planId)}
                  />
                </Card>

                {activationTotal > ACTIVATION_PAGE_SIZE ? (
                  <Pagination
                    current={activationPage + 1}
                    pageSize={ACTIVATION_PAGE_SIZE}
                    total={activationTotal}
                    showSizeChanger={false}
                    onChange={(p) => setActivationPage(p - 1)}
                  />
                ) : null}
              </Flex>
            ),
          },
        ]}
      />

      <BatchCodesDrawer
        batch={openBatch}
        planName={openBatch === null ? null : planLabelFor(openBatch.planId)}
        onClose={() => setOpenBatch(null)}
        onPrint={(serials) => setPrintRequest({ serials })}
      />

      <VoucherPrintSheet
        batch={printRequest === null ? null : openBatch}
        serials={printRequest?.serials ?? null}
        planName={openBatch === null ? null : planLabelFor(openBatch.planId)}
        onClose={() => setPrintRequest(null)}
      />
    </div>
  )
}
